/**
 * V2CopyBlockSection — V2-native editor section for step.content.copy + hero
 *
 * Edits: Title, Subtitle, Detail text + styles; Hero image (for applicable types)
 * V2 paths: content.copy.title/subtitle/detail, content.hero
 *
 * Replaces: OnboardingBodySection + OnboardingTitleSubtitleSection (for V2 context)
 * Reuses: V2TemplateTextElement, ImageUploadElement, SAccordion
 *
 * Uses V2TemplateTextElement for rich text editing with @link, <tag>label</tag>,
 * and @flowVariable support. Flow variables come from VariableEditorContext.
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import React, { useCallback, useState, useMemo } from 'react';
import { SAccordion } from '@modules/ui-components';
import { V2TemplateTextElement } from '../../shared/templateFields/V2TemplateTextElement';
import { ImageUploadElement } from '../../../factories/elements/ImageUploadElement';
import { EditorGuideProvider } from '../../../contexts';
import { buildFlowVariableItems } from '../../../config/flowVariablesConfig';
import { variableResolver } from '@modules/v2-variable-utils';
import { OnboardingMediaUploadService } from '@modules/firebase-core/services/OnboardingMediaUploadService';
import type { FlowDocument } from '@segmently/schema-models/v2';
import {
  V2_TITLE_CONFIG,
  V2_SUBTITLE_CONFIG,
  V2_HERO_IMAGE_CONFIG,
  HERO_SCREEN_TYPES,
} from '../../../config/v2CopyBlockConfig';
import { V2_HERO_SUBTYPE_OPTIONS } from '../../../config/v2StepperConfig';
import { SECTION_MARGIN_BOTTOM } from '../../../config/ui';
import { getValueByPath, isFieldEnabled } from '../../../utils/v2FieldOperations';
import {
  getStyleValues as getStyleValuesUtil,
} from '../../../utils/fieldValueOperations';
import {
  getSectionTestId,
  getFieldTestId,
  getCheckboxTestId,
  getStylesToggleTestId,
  getInputTestId,
  getUploadButtonTestId,
  getPreviewTestId,
} from '../../../utils/testIdUtils';
import type { FieldConfig } from '../../../types/editorFieldTypes';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import { getThemedTitleDefaults, getThemedSubtitleDefaults } from '../../../utils/v2ThemeDefaults';

export interface V2CopyBlockSectionProps {
  /** V2 StepNode being edited */
  step: FlowStepNode;

  /** Current editing locale */
  locale: string;

  /** Callback when a V2 path value changes */
  onChange: (path: string, value: unknown) => void;

  /** Whether section is disabled (saving state) */
  disabled?: boolean;

  /** Whether section is expanded by default */
  defaultExpanded?: boolean;

  /** Project ID for upload service */
  projectId?: string;

  /** Onboarding ID for upload service */
  onboardingId?: string;

  /** Version ID for upload service */
  versionId?: string;

  /** Available locales for multi-locale upload dialog */
  availableLocales?: string[];

  /** Full FlowDocument for BFS-based variable resolution (only show variables reachable at this screen) */
  flowDocument?: FlowDocument | null;

  /** Theme settings for themed defaults on element toggle ON */
  themeSettingsV2?: OnboardingThemeSettingsV2 | null;
}

export const V2CopyBlockSection: React.FC<V2CopyBlockSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  projectId,
  onboardingId,
  versionId,
  flowDocument,
  themeSettingsV2,
}) => {
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);

  const uploadService = useMemo(() => new OnboardingMediaUploadService(), []);

  // Build flow variable items from BFS-reachable variables at this screen
  // Uses variableResolver to find only variables bound by predecessor screens
  const flowVariableItems = useMemo(
    () => {
      if (!flowDocument || !step.id) return [];
      const boundVars = variableResolver.findAvailableVariables(flowDocument, step.id);
      // Convert BoundVariable[] → Record<string, FunnelVariable> for buildFlowVariableItems
      const varsRecord: Record<string, import('@modules/v2-variable-utils').FunnelVariable> = {};
      for (const bv of boundVars) {
        varsRecord[bv.variable.id] = bv.variable;
      }
      return buildFlowVariableItems(varsRecord);
    },
    [flowDocument, step.id],
  );

  // Determine if this screen type shows hero image
  const showHero = useMemo(() => {
    return step.screenType ? HERO_SCREEN_TYPES.has(step.screenType) : false;
  }, [step]);

  const toggleFieldExpansion = useCallback((path: string) => {
    setExpandedFields(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const checkFieldEnabled = useCallback(
    (config: FieldConfig): boolean => isFieldEnabled(step, config.path),
    [step]
  );

  const getStyleValues = useCallback(
    (config: FieldConfig): Record<string, unknown> => getStyleValuesUtil(step as unknown as Record<string, unknown>, config),
    [step]
  );

  const handleEnabledChange = useCallback(
    (config: FieldConfig, enabled: boolean) => {
      if (enabled) {
        const defaultText = (config.defaultValue as string) || '';
        if (themeSettingsV2) {
          // Use themed defaults: title gets 100% color, subtitle gets 50% alpha
          const isSubtitle = config.path.includes('subtitle');
          const themed = isSubtitle
            ? getThemedSubtitleDefaults(themeSettingsV2, locale, defaultText)
            : getThemedTitleDefaults(themeSettingsV2, locale, defaultText);
          onChange(config.path, themed);
        } else {
          onChange(config.path, {
            kind: 'static',
            translations: { [locale]: defaultText },
            appearance: {},
          });
        }
      } else {
        onChange(config.path, null);
      }
    },
    [onChange, locale, themeSettingsV2]
  );

  const handleStyleChange = useCallback(
    (stylePath: string, value: unknown) => onChange(stylePath, value),
    [onChange]
  );

  // Hero image handlers
  const heroUrl = useMemo(() => {
    if (!showHero) return null;
    // V2 hero uses translations for localized images
    const localized = getValueByPath<Record<string, unknown>>(step, `content.hero.translations.${locale}`);
    if (localized && typeof localized === 'object') {
      return (localized as Record<string, string>).original ?? null;
    }
    return getValueByPath<string>(step, 'content.hero.translations.' + locale) ?? null;
  }, [step, locale, showHero]);

  const isHeroEnabled = useMemo(() => {
    return isFieldEnabled(step, 'content.hero');
  }, [step]);

  const handleHeroChange = useCallback(
    (value: string | null) => {
      if (value) {
        onChange(`content.hero.translations.${locale}`, { original: value, small: value });
      } else {
        onChange(`content.hero.translations.${locale}`, null);
      }
    },
    [onChange, locale]
  );

  const handleHeroEnabledChange = useCallback(
    (enabled: boolean) => {
      onChange('content.hero', enabled ? { translations: {}, appearance: {} } : null);
    },
    [onChange]
  );

  const handleHeroUpload = useCallback(
    async (file: File): Promise<string> => {
      setUploading(true);
      try {
        const result = await uploadService.uploadImage(file, {
          projectId: projectId!,
          onboardingId: onboardingId!,
          versionId: versionId!,
          locale,
        });
        onChange(`content.hero.translations.${locale}`, {
          original: result.assetUrl.origin,
          small: result.assetUrl.small,
        });
        return result.assetUrl.origin;
      } finally {
        setUploading(false);
      }
    },
    [uploadService, projectId, onboardingId, versionId, locale, onChange]
  );

  // Shared props builder for V2TemplateTextElement fields
  const buildFieldProps = useCallback(
    (config: FieldConfig) => ({
      enabled: checkFieldEnabled(config),
      styleValues: getStyleValues(config),
      expandedStyles: expandedFields.has(config.path),
    }),
    [checkFieldEnabled, getStyleValues, expandedFields],
  );

  const titleFieldProps = useMemo(() => buildFieldProps(V2_TITLE_CONFIG), [buildFieldProps]);
  const subtitleFieldProps = useMemo(() => buildFieldProps(V2_SUBTITLE_CONFIG), [buildFieldProps]);

  // Image Layout subtype (HeroContent only)
  const isHeroContent = step.screenType === 'HeroContent';
  const heroImageConfig = useMemo<FieldConfig>(() => ({
    ...V2_HERO_IMAGE_CONFIG,
    styleFields: [
      ...(isHeroContent
        ? [{
            path: 'subtype',
            label: 'Image Layout',
            type: 'select' as const,
            options: [...V2_HERO_SUBTYPE_OPTIONS],
            defaultValue: 'heroSlot1',
            isNullable: false,
          }]
        : []),
      ...(V2_HERO_IMAGE_CONFIG.styleFields || []),
    ],
  }), [isHeroContent]);

  const heroImageStyleValues = useMemo(
    () => getStyleValuesUtil(step as unknown as Record<string, unknown>, heroImageConfig),
    [step, heroImageConfig],
  );

  const SECTION_NAME = 'Content';

  return (
    <SAccordion
      title={SECTION_NAME}
      collapsible
      defaultExpanded={defaultExpanded}
      sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
      data-testid={getSectionTestId(SECTION_NAME)}
    >
      <EditorGuideProvider>
        {/* Hero Image (only for HeroContent, WheelPicker, HeroList) */}
        {showHero && (
          <ImageUploadElement
            fieldConfig={heroImageConfig}
            value={heroUrl}
            templateValue={null}
            enabled={isHeroEnabled}
            locale={locale}
            onChange={handleHeroChange}
            onEnabledChange={handleHeroEnabledChange}
            onUpload={handleHeroUpload}
            disabled={disabled || uploading}
            expandedStyles={expandedFields.has(heroImageConfig.path)}
            onStylesExpandToggle={() => toggleFieldExpansion(heroImageConfig.path)}
            styleValues={heroImageStyleValues}
            onStyleChange={handleStyleChange}
            data-testid={getFieldTestId(SECTION_NAME, heroImageConfig.label)}
            checkboxTestId={getCheckboxTestId(SECTION_NAME, heroImageConfig.label)}
            stylesToggleTestId={getStylesToggleTestId(SECTION_NAME, heroImageConfig.label)}
            inputTestId={getInputTestId(SECTION_NAME, heroImageConfig.label)}
            uploadButtonTestId={getUploadButtonTestId(SECTION_NAME, heroImageConfig.label)}
            previewTestId={getPreviewTestId(SECTION_NAME, heroImageConfig.label)}
          />
        )}

        {/* Title */}
        <V2TemplateTextElement
          step={step}
          fieldConfig={V2_TITLE_CONFIG}
          locale={locale}
          onChange={onChange}
          {...titleFieldProps}
          onEnabledChange={(enabled) => handleEnabledChange(V2_TITLE_CONFIG, enabled)}
          onStylesExpandToggle={() => toggleFieldExpansion(V2_TITLE_CONFIG.path)}
          onStyleChange={handleStyleChange}
          disabled={disabled}
          screenType={step.screenType}
          flowVariableItems={flowVariableItems}
          data-testid={getFieldTestId(SECTION_NAME, V2_TITLE_CONFIG.label)}
          checkboxTestId={getCheckboxTestId(SECTION_NAME, V2_TITLE_CONFIG.label)}
          stylesToggleTestId={getStylesToggleTestId(SECTION_NAME, V2_TITLE_CONFIG.label)}
          inputTestId={getInputTestId(SECTION_NAME, V2_TITLE_CONFIG.label)}
          styleGroupTestId="styles-group-content-title"
        />

        {/* Subtitle */}
        <V2TemplateTextElement
          step={step}
          fieldConfig={V2_SUBTITLE_CONFIG}
          locale={locale}
          onChange={onChange}
          {...subtitleFieldProps}
          onEnabledChange={(enabled) => handleEnabledChange(V2_SUBTITLE_CONFIG, enabled)}
          onStylesExpandToggle={() => toggleFieldExpansion(V2_SUBTITLE_CONFIG.path)}
          onStyleChange={handleStyleChange}
          disabled={disabled}
          screenType={step.screenType}
          flowVariableItems={flowVariableItems}
          data-testid={getFieldTestId(SECTION_NAME, V2_SUBTITLE_CONFIG.label)}
          checkboxTestId={getCheckboxTestId(SECTION_NAME, V2_SUBTITLE_CONFIG.label)}
          stylesToggleTestId={getStylesToggleTestId(SECTION_NAME, V2_SUBTITLE_CONFIG.label)}
          inputTestId={getInputTestId(SECTION_NAME, V2_SUBTITLE_CONFIG.label)}
          styleGroupTestId="styles-group-content-subtitle"
        />

      </EditorGuideProvider>
    </SAccordion>
  );
};
